// Appels — /voice-tickets. The phone calls answered by the voice agents:
// « À vérifier » when no call came in for days (the voice gateway went
// silent for months once, KI-164), the period's figures (calls, average
// duration, calls where the agent acted, tickets opened), then the calls
// (caller, outcome, line, date, duration) with search, outcome filter, line
// filter and « Charger plus ». A row opens the call (/voice-tickets/:callId).
// No default date window: a tenant's calls may all be months old, and an
// empty page reads as broken — the period is opt-in (« Tout » first).
// API: GET /api/ai-voice/conversations?limit&skip&start_date&end_date
// (total + rows), GET /api/voice-agents (line names: calls answered by a
// voice agent come back without assistant_name; silent lines).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarRange, Clock, Headphones, PhoneCall, RefreshCw, Search, Sparkles, Ticket,
} from 'lucide-react';
import {
  Badge, Banner, EmptyState, Kpi, PageHeader, Segmented,
} from '../design/ui';
import { callerInitials, formatCallDay, formatRelative } from '../utils/voiceFormat';
import { canManage, formatDay, formatDuration, listAgents } from './voiceSetup/api';
import {
  agentIndex, callKind, callOutcomes, callsAttention, lineAgentName, summarizeCalls,
} from './voiceSetup/voiceModel';
import { AttentionCard, durationTile, formatCount, pluralT } from './voiceSetup/VoiceKit';
import { formatPhone, numberLabel, phoneMatches } from './voiceSetup/phoneFormat';
import './voiceSetup/VoiceSetup.css';
import './VoiceTickets.css';

const PAGE_SIZE = 50;
const OUTCOME_TONE = { action: 'success', escalation: 'warning', lookup: undefined };

// A YYYY-MM-DD day in Paris, `daysAgo` days before today.
const parisDay = (daysAgo) => new Date(Date.now() - daysAgo * 86400000).toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' });

const durationText = (seconds) => (seconds === null || seconds === undefined ? '—' : formatDuration(seconds));

const VoiceTickets = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language || 'fr';
  const tp = pluralT(t);
  const navigate = useNavigate();
  const manage = canManage();
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState('');
  const [period, setPeriod] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [line, setLine] = useState('');
  const [facet, setFacet] = useState('all');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [agents, setAgents] = useState([]);

  const token = localStorage.getItem('access_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  let rangeStart = '';
  let rangeEnd = '';
  if (period === '7') rangeStart = parisDay(6);
  else if (period === '30') rangeStart = parisDay(29);
  else if (period === 'custom') { rangeStart = startDate; rangeEnd = endDate; }
  const filtered = Boolean(rangeStart || rangeEnd);

  const fetchConversations = useCallback(async (skip = 0) => {
    const setBusy = skip > 0 ? setLoadingMore : setLoading;
    setBusy(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('limit', String(PAGE_SIZE));
      if (skip > 0) params.append('skip', String(skip));
      if (rangeStart) params.append('start_date', rangeStart);
      if (rangeEnd) params.append('end_date', rangeEnd);
      const response = await axios.get(`/api/ai-voice/conversations?${params.toString()}`, { headers });
      const rows = response.data?.conversations || [];
      setTotal(response.data?.total ?? rows.length);
      setConversations((prev) => (skip > 0 ? [...prev, ...rows] : rows));
    } catch (err) {
      console.error('Failed to fetch voice conversations:', err);
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('voiceTickets.fetchError', 'Échec du chargement des conversations vocales.'));
      if (skip === 0) setConversations([]);
    } finally {
      setBusy(false);
      setLoaded(true);
    }
  }, [headers, rangeStart, rangeEnd, t]);

  useEffect(() => {
    fetchConversations(0);
  }, [fetchConversations]);

  // The voice agents name the lines (and say which line went silent).
  useEffect(() => {
    let alive = true;
    listAgents()
      .then((res) => alive && setAgents(res.data?.agents || []))
      .catch(() => alive && setAgents([]));
    return () => { alive = false; };
  }, []);

  const index = useMemo(() => agentIndex(agents), [agents]);

  // The phone lines present in the loaded calls (a line = a voice agent).
  const lines = useMemo(() => {
    const byNumber = new Map();
    conversations.forEach((conv) => {
      if (conv.dialed_number && !byNumber.has(conv.dialed_number)) {
        byNumber.set(conv.dialed_number, lineAgentName(conv, index) || formatPhone(conv.dialed_number));
      }
    });
    return Array.from(byNumber, ([number, label]) => ({ number, label }));
  }, [conversations, index]);

  const searched = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return conversations.filter((conv) => {
      if (line && conv.dialed_number !== line) return false;
      if (!needle) return true;
      return [conv.caller_name, conv.phone_number, formatPhone(conv.phone_number), conv.preview]
        .some((field) => (field || '').toLowerCase().includes(needle))
        || phoneMatches(conv.phone_number, needle);
    });
  }, [conversations, query, line]);

  const facetCounts = useMemo(() => {
    const counts = { all: searched.length, action: 0, escalated: 0, info: 0 };
    searched.forEach((conv) => { counts[callKind(conv.tool_names)] += 1; });
    return counts;
  }, [searched]);

  const visible = useMemo(() => (
    facet === 'all' ? searched : searched.filter((conv) => callKind(conv.tool_names) === facet)
  ), [searched, facet]);

  const summary = useMemo(() => summarizeCalls(conversations), [conversations]);
  // No call at all yet: the empty state alone, no filters to play with.
  const noCallsYet = loaded && !loading && !error && !filtered && total === 0;
  const partial = conversations.length < total;

  const openCall = (callId) => navigate(`/voice-tickets/${encodeURIComponent(callId)}`);

  const attention = (loaded && !loading ? callsAttention({ calls: conversations, filtered, agents }) : []).map((item) => {
    if (item.kind === 'silentAll') {
      return {
        ...item,
        title: t('voiceUi.calls.attSilent', 'Aucun appel depuis le {{date}}', { date: formatDay(item.last, lang) }),
        detail: tp(
          'voiceUi.calls.attSilentDetail',
          '{{count}} jour sans appel reçu par vos agents vocaux : appelez un de vos numéros pour vérifier qu’un agent décroche.',
          '{{count}} jours sans appel reçu par vos agents vocaux : appelez un de vos numéros pour vérifier qu’un agent décroche.',
          { count: item.days },
        ),
        action: manage ? { label: t('voiceUi.calls.seeNumbers', 'Voir les numéros'), onClick: () => navigate('/voice-numbers') } : null,
      };
    }
    return {
      ...item,
      title: t('voiceUi.attention.agentSilent', 'Aucun appel pour {{name}} depuis le {{date}}', { name: item.agent.name, date: formatDay(item.last, lang) }),
      detail: tp(
        'voiceUi.attention.silentDetail',
        '{{count}} jour sans appel sur le {{number}} : appelez-le pour vérifier que l’agent décroche.',
        '{{count}} jours sans appel sur le {{number}} : appelez-le pour vérifier que l’agent décroche.',
        { count: item.days, number: item.agent.numbers.map(numberLabel).join(', ') },
      ),
      action: manage ? { label: t('voiceUi.attention.openAgent', 'Ouvrir l’agent'), onClick: () => navigate(`/voice-agents/${item.agent.id}`) } : null,
    };
  });

  let periodScope = t('voiceUi.calls.scopeAll', 'depuis le début');
  if (period === '7') periodScope = t('voiceUi.calls.scope7', 'sur 7 jours');
  else if (period === '30') periodScope = t('voiceUi.calls.scope30', 'sur 30 jours');
  else if (period === 'custom' && (startDate || endDate)) {
    periodScope = t('voiceUi.calls.scopeCustom', 'du {{from}} au {{to}}', {
      from: startDate ? formatDay(startDate, lang) : '…',
      to: endDate ? formatDay(endDate, lang) : t('voiceUi.calls.today', 'aujourd’hui'),
    });
  }

  let subtitle = t('voiceTickets.subtitle', 'Les conversations téléphoniques traitées par vos agents vocaux.');
  if (loaded && !loading && total > 0) {
    subtitle = [
      tp('voiceUi.calls.subtitleCount', '{{count}} appel reçu par vos agents vocaux {{scope}}', '{{count}} appels reçus par vos agents vocaux {{scope}}', { count: total, scope: periodScope }),
      summary.latest && t('voiceUi.lastCall', 'dernier {{when}}', { when: formatRelative(summary.latest) }),
    ].filter(Boolean).join(' · ');
  }

  // Duration, actions and tickets are counted on the loaded calls: say so
  // when « Charger plus » would add more.
  const sampleScope = partial
    ? tp('voiceUi.calls.sampleScope', 'sur le dernier appel', 'sur les {{count}} derniers appels', { count: conversations.length })
    : t('voiceUi.calls.periodScope', 'sur la période');
  const share = (count) => (summary.count ? Math.round((count / summary.count) * 100) : 0);

  const renderKpis = () => {
    if (!loaded || loading) {
      return (
        <div className="u-kpi-row" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton voice-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (!total) return null;
    return (
      <div className="u-kpi-row">
        <Kpi
          icon={PhoneCall}
          tone="info"
          label={t('voiceUi.calls.kpiCalls', 'Appels')}
          value={formatCount(total, lang)}
          context={periodScope}
          active={facet === 'all'}
          onClick={() => setFacet('all')}
        />
        {summary.avgDuration !== null && (
          <Kpi
            icon={Clock}
            label={t('voiceUi.kpiAvgDuration', 'Durée moyenne')}
            {...durationTile(summary.avgDuration)}
            context={sampleScope}
          />
        )}
        <Kpi
          icon={Sparkles}
          tone="success"
          label={t('voiceUi.calls.kpiActions', 'L’agent a agi')}
          value={`${share(summary.action)} %`}
          context={partial
            ? tp('voiceUi.calls.kpiActionsPartial', '{{count}} appel sur les {{n}} derniers', '{{count}} appels sur les {{n}} derniers', { count: summary.action, n: summary.count })
            : tp('voiceUi.calls.kpiActionsAll', '{{count}} appel avec une action', '{{count}} appels avec une action', { count: summary.action })}
          meter={{ value: summary.action, max: summary.count, tone: 'success', label: t('voiceUi.calls.kpiActions', 'L’agent a agi') }}
          active={facet === 'action'}
          onClick={() => setFacet(facet === 'action' ? 'all' : 'action')}
          title={t('voiceUi.calls.kpiActionsTitle', 'Afficher les appels avec une action')}
        />
        <Kpi
          icon={Ticket}
          label={t('voiceUi.calls.kpiTickets', 'Tickets ouverts')}
          value={formatCount(summary.tickets, lang)}
          context={partial
            ? t('voiceUi.calls.kpiTicketsPartial', '{{share}} % des {{n}} derniers appels', { share: share(summary.tickets), n: summary.count })
            : t('voiceUi.calls.kpiTicketsAll', '{{share}} % des appels', { share: share(summary.tickets) })}
        />
      </div>
    );
  };

  const facetOptions = [
    { value: 'all', label: t('voiceTickets.facetAll', 'Tous'), count: facetCounts.all },
    { value: 'action', label: t('voiceTickets.facetAction', 'Avec action'), count: facetCounts.action },
    (facetCounts.escalated > 0 || facet === 'escalated') && { value: 'escalated', label: t('voiceTickets.facetEscalated', 'Escaladés'), count: facetCounts.escalated },
    { value: 'info', label: t('voiceTickets.facetInfo', 'Info seule'), count: facetCounts.info },
  ].filter(Boolean);

  const renderRows = () => {
    if (loading) {
      return (
        <div className="voice-v2__list-skeleton voice-v2__list-skeleton--inset" aria-busy="true" aria-label={t('voiceTickets.loading', 'Chargement des conversations…')}>
          {Array.from({ length: 6 }, (_, i) => <span key={i} className="u-skeleton" />)}
        </div>
      );
    }
    if (!conversations.length) {
      return filtered ? (
        <EmptyState
          icon={CalendarRange}
          title={t('voiceUi.calls.emptyPeriod', 'Aucun appel sur cette période')}
          action={(
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setPeriod('all')}>
              {t('voiceUi.calls.showAll', 'Voir tous les appels')}
            </button>
          )}
        >
          {t('voiceUi.calls.emptyPeriodHint', 'Élargissez la période pour retrouver les appels plus anciens.')}
        </EmptyState>
      ) : (
        <EmptyState
          icon={PhoneCall}
          title={t('voiceUi.calls.emptyTitle', 'Aucun appel pour l’instant')}
          action={manage ? (
            <Link className="u-btn u-btn--secondary u-btn--sm" to="/voice-agents">
              <Headphones aria-hidden="true" />
              {t('voiceUi.calls.setupAgents', 'Mettre en place un agent vocal')}
            </Link>
          ) : undefined}
        >
          {t('voiceUi.calls.emptyHint', 'Les appels décrochés par vos agents vocaux arrivent ici, avec leur transcription et ce que l’agent a fait.')}
        </EmptyState>
      );
    }
    if (!visible.length) {
      return (
        <EmptyState
          icon={Search}
          title={t('voiceTickets.empty', 'Aucune conversation vocale trouvée.')}
          action={(
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => { setQuery(''); setFacet('all'); setLine(''); }}>
              {t('voiceUi.calls.clearFilters', 'Effacer les filtres')}
            </button>
          )}
        >
          {t('voiceUi.calls.emptyFilteredHint', 'Changez la recherche ou le filtre pour voir les autres appels.')}
        </EmptyState>
      );
    }
    return (
      <div className="u-table-scroll">
        <table className="u-table voice-calls__table">
          <thead>
            <tr>
              <th style={{ width: '24%' }}>{t('voiceTickets.phoneNumber', 'Appelant')}</th>
              <th style={{ width: '34%' }}>{t('voiceUi.calls.colOutcome', 'Ce qui s’est passé')}</th>
              <th style={{ width: '16%' }}>{t('voiceTickets.line', 'Ligne')}</th>
              <th style={{ width: '14%' }}>{t('voiceUi.calls.colDate', 'Date')}</th>
              <th style={{ width: '12%' }}>{t('voiceTickets.duration', 'Durée')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((conv) => {
              const outcomes = callOutcomes(conv.tool_names, t);
              const lineName = lineAgentName(conv, index);
              const phone = formatPhone(conv.phone_number);
              const dialed = formatPhone(conv.dialed_number);
              const callerLabel = conv.caller_name || phone || t('voiceTickets.detail.unknownCaller', 'Appelant inconnu');
              return (
                <tr
                  key={conv.call_id}
                  className="voice-calls__row"
                  tabIndex={0}
                  onClick={() => openCall(conv.call_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openCall(conv.call_id); }
                  }}
                >
                  <td title={[conv.caller_name, phone].filter(Boolean).join(' · ')}>
                    <span className="voice-calls__caller">
                      <span className={`u-avatar u-avatar--sm voice-calls__avatar${conv.caller_name ? '' : ' is-unknown'}`} aria-hidden="true">
                        {callerInitials(conv.caller_name, conv.phone_number)}
                      </span>
                      <span className="voice-calls__caller-text">
                        <span className={`voice-calls__name${conv.caller_name ? '' : ' u-num'}`}>{callerLabel}</span>
                        <span className="u-cell-sub u-num">
                          {conv.caller_name ? (phone || '—') : t('voiceTickets.unknownCallerSub', 'appelant inconnu')}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td title={conv.preview || ''}>
                    <span className="voice-calls__outcomes">
                      {outcomes.slice(0, 2).map((outcome) => (
                        <Badge key={outcome.name} tone={OUTCOME_TONE[outcome.kind]}>{outcome.label}</Badge>
                      ))}
                      {outcomes.length > 2 && <span className="voice-calls__more">+{outcomes.length - 2}</span>}
                      {outcomes.length === 0 && <Badge>{t('voiceTickets.facetInfo', 'Info seule')}</Badge>}
                    </span>
                    {conv.preview && <span className="u-cell-sub voice-calls__preview">« {conv.preview} »</span>}
                  </td>
                  <td title={[lineName, dialed].filter(Boolean).join(' · ')}>
                    <span className={`voice-calls__line${lineName ? '' : ' u-num'}`}>{lineName || dialed || '—'}</span>
                    {lineName && dialed && <span className="u-cell-sub u-num">{dialed}</span>}
                  </td>
                  <td>
                    <span className="u-num">{formatCallDay(conv.started_at)}</span>
                    <span className="u-cell-sub">{formatRelative(conv.started_at)}</span>
                  </td>
                  <td>
                    <span className="u-num">{durationText(conv.duration_seconds)}</span>
                    <span className="u-cell-sub">{t('voiceTickets.turnsCount', '{{n}} échanges', { n: conv.turn_count ?? 0 })}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="u-page voice-v2 voice-calls">
      <PageHeader
        title={t('voiceUi.calls.title', 'Appels')}
        subtitle={subtitle}
        actions={(
          <>
            <button
              type="button"
              className="u-btn u-btn--secondary u-btn--icon"
              onClick={() => fetchConversations(0)}
              disabled={loading}
              title={t('voiceTickets.refresh', 'Actualiser')}
              aria-label={t('voiceTickets.refresh', 'Actualiser')}
            >
              <RefreshCw aria-hidden="true" />
            </button>
            {manage && (
              <Link className="u-btn u-btn--secondary" to="/voice-agents">
                <Headphones aria-hidden="true" />
                {t('voiceUi.numbers.agentsLink', 'Agents vocaux')}
              </Link>
            )}
          </>
        )}
      />

      <div className="u-well">
        {error && <Banner tone="danger">{error}</Banner>}
        <AttentionCard items={attention} t={t} />

        {!noCallsYet && (
          <div className="voice-calls__period">
            <Segmented
              label={t('voiceUi.calls.period', 'Période')}
              value={period}
              onChange={setPeriod}
              options={[
                { value: 'all', label: t('voiceUi.calls.periodAll', 'Tout') },
                { value: '7', label: t('voiceUi.calls.period7', '7 jours') },
                { value: '30', label: t('voiceUi.calls.period30', '30 jours') },
                { value: 'custom', label: t('voiceUi.calls.periodCustom', 'Dates') },
              ]}
            />
            {period === 'custom' && (
              <span className="voice-calls__dates">
                <input
                  type="date"
                  className="u-input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate || undefined}
                  aria-label={t('voiceTickets.startDate', 'Début')}
                />
                <span aria-hidden="true">–</span>
                <input
                  type="date"
                  className="u-input"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || undefined}
                  aria-label={t('voiceTickets.endDate', 'Fin')}
                />
              </span>
            )}
          </div>
        )}

        {renderKpis()}

        <section className="u-card">
          {!noCallsYet && (
            <div className="u-toolbar">
              <div className="u-input-group u-input-group--lead">
                <Search className="u-input-group__icon" aria-hidden="true" />
                <input
                  className="u-input"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('voiceTickets.searchPlaceholder', 'Rechercher un nom ou un numéro…')}
                  aria-label={t('voiceTickets.search', 'Rechercher')}
                />
              </div>
              {conversations.length > 0 && (
                <Segmented
                  label={t('voiceTickets.facets', 'Type d’appel')}
                  value={facet}
                  onChange={setFacet}
                  options={facetOptions}
                />
              )}
              <span className="u-toolbar__spacer" />
              {lines.length > 1 && (
                <select
                  className="u-select"
                  value={line}
                  onChange={(e) => setLine(e.target.value)}
                  aria-label={t('voiceTickets.line', 'Ligne')}
                >
                  <option value="">{t('voiceTickets.allLines', 'Toutes les lignes')}</option>
                  {lines.map((item) => (
                    <option key={item.number} value={item.number}>{item.label}</option>
                  ))}
                </select>
              )}
            </div>
          )}
          {renderRows()}
          {!loading && conversations.length > 0 && (
            <div className="u-pagination">
              <span>
                {partial
                  ? t('voiceUi.calls.shown', '{{shown}} affichés sur {{total}}', { shown: formatCount(conversations.length, lang), total: formatCount(total, lang) })
                  : tp('voiceUi.calls.countAll', '{{count}} appel', '{{count}} appels', { count: total })}
              </span>
              {partial && (
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => fetchConversations(conversations.length)} disabled={loadingMore}>
                  {loadingMore ? t('voiceTickets.loadingMore', 'Chargement…') : t('voiceTickets.loadMore', 'Charger plus')}
                </button>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default VoiceTickets;
